import { filter, uniq } from "lodash-es";
import { useEffect } from "react";
import { action, computed, makeObservable, observable } from "mobx";

import { MultiSelectActions } from "@/components/multi-select-actions/multi-select-actions";
import { APPEARANCES_WITHOUT_SEPARATOR_SET } from "@/components/item-list/constants";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { AddToCollectionModalStore } from "@/components/modal/add-to-collection";
import { AddToCollectionModal } from "@/components/modal/add-to-collection";
import { RemoveNotesFromSharedCollectionModal } from "@/components/modal/remove-notes-from-shared-collection";
import { RemoveNotesFromSharedCollectionModalStore } from "@/components/modal/remove-notes-from-shared-collection";
import { DeleteSharedNotesModalStore } from "@/components/modal/delete-shared-notes";
import { INoteObservable } from "@/store/note";
import { actions } from "@/actions";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { DeleteSharedNotesModal } from "@/components/modal/delete-shared-notes";
import { DeleteNotePermanentlyModalStore } from "@/components/modal/delete-notes-permanently/delete-notes-permanently-modal-store";
import {
  IItemListState,
  ItemListHighlightOptions,
  ItemListPointerHoverEvent,
} from "@/components/item-list/types";
import { EventContext } from "@/domains/metrics/context";
import { MDSRowItemAppearance } from "@/mds";
import { itemsModule } from "@/modules/items";
import { notesModule } from "@/modules/notes";
import { toastModule } from "@/modules/toast";
import { DeleteCollectionsModal } from "@/components/modal/delete-collections";
import { DeleteCollectionsModalStore } from "@/components/modal/delete-collections";

interface ItemListPointerPosition {
  clientX: number;
  clientY: number;
}

const toItemListPointerPosition = (
  pointerEvent: ItemListPointerHoverEvent
): ItemListPointerPosition => ({
  clientX: pointerEvent.clientX,
  clientY: pointerEvent.clientY,
});

export interface ListStateProvider {
  orderedItemIds: string[];
  collectionMultiSelectActionsContext?: CollectionObservable;
  supportsCopyNoteAsMarkdownListAction?: boolean;
  supportsMoveNoteToTrashListAction?: boolean;
  supportsDeleteCollectionsListAction?: boolean;
  supportsRestoreListAction?: boolean;
  supportsAddToCollectionListAction?: boolean;
  supportsRemoveFromCollectionListAction?: boolean;
  supportsChatListAction?: boolean;
  supportsAutoOrganizeListAction?: boolean;
  getAutoOrganizeNoteIdsFromSelectedItems?: (selectedItemIds: string[]) => string[];
  getChatNoteIdsFromSelectedItems?: (selectedItemIds: string[]) => string[];
  getChatCollectionIdsFromSelectedItems?: (selectedItemIds: string[]) => string[];
  eventContext: EventContext;
}

export class ListStateObservable extends AppSubStore implements IItemListState {
  anchorSelectedItemId?: string;
  selectedItemIds: string[] = [];
  hoveredItemId?: string;
  keyboardHighlightedItemId?: string;
  isHighlightedItemPreviewOpen = false;
  private latestPointerPosition?: ItemListPointerPosition;
  private pointerPositionAtKeyboardHighlight?: ItemListPointerPosition;
  private suppressPointerHoverUntilMove = false;
  listStateProvider: ListStateProvider;
  deleteNotePermanentlyModal?: DeleteNotePermanentlyModalStore;
  isCopying = false;
  readonly canSelect: boolean;

  constructor({
    listStateProvider,
    deleteNotePermanentlyModal,
    canSelect = true,
    ...injectedDeps
  }: AppSubStoreArgs & {
    listStateProvider: ListStateProvider;
    deleteNotePermanentlyModal?: DeleteNotePermanentlyModalStore;
    canSelect?: boolean;
  }) {
    super(injectedDeps);

    this.deleteNotePermanentlyModal = deleteNotePermanentlyModal;
    this.listStateProvider = listStateProvider;
    this.canSelect = canSelect;

    makeObservable<
      this,
      | "addToCollectionModalStore"
      | "deleteSharedNotesModalStore"
      | "removeFromCollectionModalStore"
      | "deleteCollectionsModalStore"
      | "latestPointerPosition"
      | "pointerPositionAtKeyboardHighlight"
      | "suppressPointerHoverUntilMove"
      | "shouldIgnorePointerHover"
    >(this, {
      getNextItemId: true,
      shouldHideSeparator: false,
      canSelect: false,
      handleDeleteCollections: true,
      anchorSelectedItemId: observable,
      selectedItemIds: observable,
      hoveredItemId: observable,
      keyboardHighlightedItemId: observable,
      isHighlightedItemPreviewOpen: observable,
      latestPointerPosition: false,
      pointerPositionAtKeyboardHighlight: false,
      suppressPointerHoverUntilMove: false,
      listStateProvider: observable,
      isCopying: observable,
      deleteNotePermanentlyModal: false,
      isHoverable: computed,
      isSelectModeActive: computed,
      highlightedItemId: computed,
      keyboardPreviewedItemId: computed,
      canAddToCollection: computed,
      canRemoveFromCollection: computed,
      supportsCopyNoteAsMarkdownListAction: computed,
      canMoveNoteToTrash: computed,
      canMoveCollectionToTrash: computed,
      canRestoreFromTrash: computed,
      canChatWithSelection: computed,
      supportsAutoOrganize: computed,
      canAutoOrganize: computed,
      selectedAutoOrganizeNoteIds: computed,
      selectedChatNoteIds: computed,
      selectedChatCollectionIds: computed,
      multiSelectUi: computed,
      isSelected: false,
      toggleItemSelection: action,
      selectItemsFromLastSelectionUntil: action,
      clearSelection: action,
      addToCollectionModalStore: false,
      deleteSharedNotesModalStore: false,
      removeFromCollectionModalStore: false,
      highlightItem: action,
      hoverItem: action,
      toggleHighlightedItemPreview: action,
      shouldIgnorePointerHover: false,
      clearHoveredItem: action,
      openAddToCollection: action,
      handleAddToCollection: action,
      handleRemoveFromCollection: action,
      handleCopyNoteAsMarkdown: action,
      handleRestore: action,
      handleDeleteNote: action,
      handleCopySelectedNotes: action,
      handleAutoOrganize: action,
      handleChatWithSelection: action,
      reset: action,
      useListStateEffects: false,
      deleteCollectionsModalStore: false,
    });
  }

  get isHoverable() {
    return this.store.publicAppStore.interface.isHoverable;
  }

  get isSelectModeActive() {
    return this.selectedItemIds.length > 0;
  }

  get highlightedItemId() {
    return this.keyboardHighlightedItemId ?? this.hoveredItemId;
  }

  get keyboardPreviewedItemId() {
    if (!this.isHighlightedItemPreviewOpen) {
      return undefined;
    }

    return this.highlightedItemId;
  }

  get canAddToCollection() {
    return this.listStateProvider.supportsAddToCollectionListAction;
  }

  get canRemoveFromCollection() {
    return (
      !!this.listStateProvider.supportsRemoveFromCollectionListAction &&
      !!this.listStateProvider.collectionMultiSelectActionsContext
    );
  }

  get supportsCopyNoteAsMarkdownListAction() {
    return this.listStateProvider.supportsCopyNoteAsMarkdownListAction;
  }

  get canMoveNoteToTrash() {
    return this.listStateProvider.supportsMoveNoteToTrashListAction;
  }

  get canMoveCollectionToTrash() {
    return this.listStateProvider.supportsDeleteCollectionsListAction;
  }

  get canRestoreFromTrash() {
    return this.listStateProvider.supportsRestoreListAction;
  }

  get canChatWithSelection() {
    return (
      this.store.floatingAgent.isMemAgentAccessEnabled &&
      !!this.listStateProvider.supportsChatListAction &&
      this.selectedItemIds.length > 0
    );
  }

  get supportsAutoOrganize() {
    if (!this.store.floatingAgent.isMemAgentAccessEnabled) {
      return false;
    }

    const supportsAutoOrganize = this.listStateProvider.supportsAutoOrganizeListAction;
    return supportsAutoOrganize ?? true;
  }

  get selectedAutoOrganizeNoteIds() {
    const noteIdsFromProvider = this.listStateProvider.getAutoOrganizeNoteIdsFromSelectedItems
      ? this.listStateProvider.getAutoOrganizeNoteIdsFromSelectedItems(this.selectedItemIds)
      : this.selectedItemIds.filter(itemId => {
          const note = this.store.notes.get(itemId);
          return !!note && !note.isTrashed && !note.isDeleted && note.canAccess;
        });

    return Array.from(new Set(noteIdsFromProvider));
  }

  get canAutoOrganize() {
    return (
      this.supportsAutoOrganize &&
      this.selectedItemIds.length > 0 &&
      this.selectedAutoOrganizeNoteIds.length === this.selectedItemIds.length
    );
  }

  get selectedChatNoteIds() {
    const noteIdsFromProvider = this.listStateProvider.getChatNoteIdsFromSelectedItems
      ? this.listStateProvider.getChatNoteIdsFromSelectedItems(this.selectedItemIds)
      : this.selectedItemIds;

    return Array.from(new Set(noteIdsFromProvider));
  }

  get selectedChatCollectionIds() {
    const collectionIdsFromProvider = this.listStateProvider.getChatCollectionIdsFromSelectedItems
      ? this.listStateProvider.getChatCollectionIdsFromSelectedItems(this.selectedItemIds)
      : this.listStateProvider.collectionMultiSelectActionsContext
        ? [this.listStateProvider.collectionMultiSelectActionsContext.id]
        : [];

    return Array.from(new Set(collectionIdsFromProvider));
  }

  get multiSelectUi() {
    return (
      <>
        <MultiSelectActions listState={this} />
        <AddToCollectionModal store={this.addToCollectionModalStore} />
        <DeleteSharedNotesModal store={this.deleteSharedNotesModalStore} />
        <RemoveNotesFromSharedCollectionModal store={this.removeFromCollectionModalStore} />
        <DeleteCollectionsModal state={this.deleteCollectionsModalStore} />
      </>
    );
  }

  isSelected = (itemId: string) => this.selectedItemIds.includes(itemId);

  getNextItemId = (itemId: string) => {
    const index = this.listStateProvider.orderedItemIds.indexOf(itemId);
    if (index === -1) return;

    return this.listStateProvider.orderedItemIds[index + 1];
  };

  shouldHideSeparator = ({
    itemId,
    appearance,
  }: {
    itemId: string;
    appearance: MDSRowItemAppearance;
  }): boolean => {
    if (itemId === this.highlightedItemId) {
      return true;
    }

    if (this.isSelected(itemId)) {
      return true;
    }

    if (APPEARANCES_WITHOUT_SEPARATOR_SET.has(appearance)) {
      return true;
    }

    const nextItemId = this.getNextItemId(itemId);
    if (!nextItemId) {
      return false;
    }

    if (nextItemId === this.highlightedItemId) {
      return true;
    }

    if (this.isSelected(nextItemId)) {
      return true;
    }

    const nextAppearance = itemsModule.resolveAppearance({
      itemId: nextItemId,
      appStore: this.store,
    });

    if (APPEARANCES_WITHOUT_SEPARATOR_SET.has(nextAppearance)) {
      return true;
    }

    const nextNote = this.store.notes.get(nextItemId);
    return Boolean(nextNote?.suggestionData);
  };

  toggleItemSelection = (itemId: string) => {
    if (!this.canSelect) {
      return;
    }

    if (this.selectedItemIds.includes(itemId)) {
      this.selectedItemIds = this.selectedItemIds.filter(e => e !== itemId);
      return;
    }
    this.anchorSelectedItemId = itemId;
    this.selectedItemIds.push(itemId);
  };

  selectItemsFromLastSelectionUntil = (itemId: string) => {
    if (!this.canSelect) {
      return;
    }

    const { orderedItemIds } = this.listStateProvider;
    const lastSelectedIndex = this.anchorSelectedItemId
      ? orderedItemIds.indexOf(this.anchorSelectedItemId)
      : 0;
    const nextSelectedIndex = orderedItemIds.indexOf(itemId);

    const [startIndex, endIndex] =
      lastSelectedIndex < nextSelectedIndex
        ? [lastSelectedIndex, nextSelectedIndex + 1]
        : [nextSelectedIndex, lastSelectedIndex + 1];

    const newSelectedItems = orderedItemIds.slice(startIndex, endIndex);
    this.selectedItemIds = uniq([...this.selectedItemIds, ...newSelectedItems]);
  };

  clearSelection = () => {
    this.anchorSelectedItemId = undefined;
    this.selectedItemIds = [];
  };

  private addToCollectionModalStore = new AddToCollectionModalStore(this, {
    handleConfirm: this.clearSelection,
  });
  private deleteSharedNotesModalStore = new DeleteSharedNotesModalStore(this, {
    handleConfirm: this.clearSelection,
  });
  private removeFromCollectionModalStore = new RemoveNotesFromSharedCollectionModalStore(this, {
    handleConfirm: this.clearSelection,
  });
  private deleteCollectionsModalStore = new DeleteCollectionsModalStore(this, {
    onClose: this.clearSelection,
  });

  highlightItem = (itemId?: string, options?: ItemListHighlightOptions) => {
    this.keyboardHighlightedItemId = itemId;
    this.hoveredItemId = itemId;

    if (!itemId) {
      this.isHighlightedItemPreviewOpen = false;
    }

    if (options?.source === "keyboard" && itemId) {
      this.suppressPointerHoverUntilMove = true;
      this.pointerPositionAtKeyboardHighlight = this.latestPointerPosition;
      return;
    }

    this.suppressPointerHoverUntilMove = false;
    this.pointerPositionAtKeyboardHighlight = undefined;
  };

  hoverItem = (itemId?: string, pointerEvent?: ItemListPointerHoverEvent) => {
    const shouldIgnoreHover = this.shouldIgnorePointerHover(itemId, pointerEvent);

    if (pointerEvent) {
      this.latestPointerPosition = toItemListPointerPosition(pointerEvent);
    }

    if (shouldIgnoreHover) {
      return;
    }

    this.hoveredItemId = itemId;
    this.keyboardHighlightedItemId = undefined;
    this.suppressPointerHoverUntilMove = false;
    this.pointerPositionAtKeyboardHighlight = undefined;
  };

  private shouldIgnorePointerHover = (
    itemId?: string,
    pointerEvent?: ItemListPointerHoverEvent
  ) => {
    if (!this.suppressPointerHoverUntilMove) {
      return false;
    }

    if (!pointerEvent) {
      return false;
    }

    if (itemId === this.keyboardHighlightedItemId) {
      return true;
    }

    if ((pointerEvent.movementX ?? 0) !== 0 || (pointerEvent.movementY ?? 0) !== 0) {
      return false;
    }

    if (!this.pointerPositionAtKeyboardHighlight) {
      return true;
    }

    return (
      pointerEvent.clientX === this.pointerPositionAtKeyboardHighlight.clientX &&
      pointerEvent.clientY === this.pointerPositionAtKeyboardHighlight.clientY
    );
  };

  clearHoveredItem = () => {
    this.hoveredItemId = undefined;

    if (!this.keyboardHighlightedItemId) {
      this.isHighlightedItemPreviewOpen = false;
    }
  };

  toggleHighlightedItemPreview = () => {
    if (!this.highlightedItemId) {
      this.isHighlightedItemPreviewOpen = false;
      return;
    }

    this.isHighlightedItemPreviewOpen = !this.isHighlightedItemPreviewOpen;
  };

  openAddToCollection = ({ noteIds }: { noteIds: string[] }) => {
    if (noteIds.length === 0) {
      return;
    }

    this.addToCollectionModalStore.open({
      noteIds,
      collection: this.listStateProvider.collectionMultiSelectActionsContext,
      eventContext: this.listStateProvider.eventContext,
    });
  };

  handleAddToCollection = () => {
    this.openAddToCollection({ noteIds: this.selectedItemIds });
  };

  handleAutoOrganize = async () => {
    if (!this.canAutoOrganize) {
      return;
    }

    const noteIds = [...this.selectedAutoOrganizeNoteIds];
    const didAutoOrganize = await actions.autoOrganizeNotes({ noteIds, store: this.store });

    if (didAutoOrganize) {
      this.clearSelection();
    }
  };

  handleChatWithSelection = async () => {
    if (!this.canChatWithSelection) {
      return;
    }

    const noteIds = this.selectedChatNoteIds;
    const collectionIds = this.selectedChatCollectionIds;

    const didStartChat = await actions.startChatWithEntities({
      noteIds,
      collectionIds,
      store: this.store,
      overrideContext: false,
    });

    if (didStartChat) {
      this.clearSelection();
    }
  };

  handleRemoveFromCollection = () => {
    const collection = this.listStateProvider.collectionMultiSelectActionsContext;
    if (!collection) return;

    if (!collection.isShared) {
      const noteIds = this.selectedItemIds;

      this.clearSelection();
      actions.removeNotesFromCollection({
        collection,
        noteIds,
        store: this.store,
      });
      return;
    }

    const notes = filter(
      this.selectedItemIds.map(noteId => this.store.notes.getNoteObservableById({ noteId }))
    ) as INoteObservable[];
    this.removeFromCollectionModalStore.open({ collection, notes });
  };

  handleCopyNoteAsMarkdown = () => {
    actions.copyNotesAsMarkdown({ noteIds: this.selectedItemIds, store: this.store });
  };

  handleRestore = () => {
    const notes = filter(
      this.selectedItemIds.map(noteId => this.store.notes.getNoteObservableById({ noteId }))
    ) as INoteObservable[];
    this.clearSelection();
    actions.restoreNotesFromTrash({ notes, store: this.store });
  };

  handleDeleteNote = () => {
    if (this.deleteNotePermanentlyModal) {
      const notes = filter(
        this.selectedItemIds.map(noteId => this.store.notes.getNoteObservableById({ noteId }))
      ) as INoteObservable[];
      this.deleteNotePermanentlyModal.open({ notes });
      return;
    }

    const notes = filter(
      this.selectedItemIds.map(noteId => this.store.notes.getNoteObservableById({ noteId }))
    ) as INoteObservable[];
    const isShared = notes.some(note => note.isShared);
    if (isShared) {
      this.deleteSharedNotesModalStore.open(...notes);
      return;
    }

    this.clearSelection();
    actions.moveNotesToTrash({ notes, store: this.store });
  };

  handleDeleteCollections = () => {
    const collections = filter(
      this.selectedItemIds.map(collectionId =>
        this.store.collections.getCollectionObservableById({ collectionId })
      )
    ) as CollectionObservable[];

    this.deleteCollectionsModalStore.open({ collectionIds: collections.map(c => c.id) });
  };

  handleCopySelectedNotes = async () => {
    if (this.isCopying) return;

    try {
      this.isCopying = true;
      // Get notes in the same order as they appear in the list
      const notes = this.listStateProvider.orderedItemIds
        .filter(id => this.selectedItemIds.includes(id))
        .map(noteId => this.store.notes.getNoteObservableById({ noteId }))
        .filter(note => note) as INoteObservable[];

      // Build XML string for each note
      const xmlStrings = await Promise.all(
        notes.map(async (note, index) => {
          // Wait for note content to load if not already loaded
          if (!note.isNoteContentDocumentLoaded) {
            await note.fetchNoteContentDocument();
          }

          // Get author names, falling back to "Unknown User" if not available
          const authors = note.authors
            .map(author => author.profileDisplayName || "Unknown User")
            .join(", ");

          // Get primary owner name, falling back to "Unknown User" if not available
          const primaryOwnerName = note.primaryOwner?.profileDisplayName || "Unknown User";

          // Get collection names
          const collections = note.collectionList?.allCollections
            .map(collection => collection.label)
            .join(", ");

          const content = note.noteContentDocument?.data?.remoteContent || "";
          const { mdx } = notesModule.convertEncodedContentToNoteContent(content);

          return `<note id="${note.id}" index="${index}">
  <head>
    <title>${note.title}</title>
    <created_at>${note.createdAt}</created_at>
    <created_by>${primaryOwnerName}</created_by>
    <last_edited_at>${note.modifiedAt}</last_edited_at>
    <edited_by>${authors}</edited_by>
    <in_collections>${collections}</in_collections>
  </head>
  <body>
    ${mdx}
  </body>
</note>`;
        })
      );

      // Combine all notes into a single XML string
      const combinedXml = xmlStrings.join("\n");

      // Add a small delay to make loading state visible
      await new Promise(resolve => setTimeout(resolve, 500));

      // Copy to clipboard
      await navigator.clipboard.writeText(combinedXml);

      // Show success toast
      toastModule.triggerToast({
        content: `${notes.length} note${notes.length === 1 ? "" : "s"} copied to clipboard`,
      });
    } finally {
      this.isCopying = false;
    }
  };

  reset = () => {
    this.anchorSelectedItemId = undefined;
    this.selectedItemIds = [];
    this.hoveredItemId = undefined;
    this.keyboardHighlightedItemId = undefined;
    this.isHighlightedItemPreviewOpen = false;
    this.addToCollectionModalStore.close();
  };

  useListStateEffects() {
    useEffect(() => () => this.reset(), []);
  }
}
